import React, {useRef, useState} from 'react';
import { Button, Spin, message} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import axios, { AxiosError } from 'axios';
import { encode } from 'blurhash';
import {api} from '../../api/api';

interface CoverPictureProps {
  src: string;
  onSuccess?: (asset: any) => void;
}

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (...args) => reject(args);
    img.src = src;
  });
};

const getImageData = (image: HTMLImageElement) => {
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  context.drawImage(image, 0, 0);
  return context.getImageData(0, 0, image.width, image.height);
};

const encodeImage = ({ data, width, height }:  ImageData) => {
  return encode(data, width, height, 4, 4)
};

export const CoverPicture: React.FC<CoverPictureProps> = ({
  src,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const openFilePicker = () => {
    if(!inputRef || !inputRef.current) {
      return;
    }

    inputRef.current.click();
  };

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading || !event || !event.target || !event.target.files) {
      return;
    }

    setIsLoading(true);

    const file = event.target.files[0];

    if (!file) {
      return;
    }

    const url = URL.createObjectURL(file);
    const image = await loadImage(url);
    const imageData = getImageData(image);
    const blurHash = encodeImage(imageData);

    try {
      // 1. First Request: Create Asset
      let assetData;
      try {
        const response = await api.post('/assets', {
          name: file.name,
          size: file.size,
          fileType: file.type,
          type: 'file',
          blurHash,
          meta: { height: image.height, width: image.width }
        });
        assetData = response.data;
      } catch (err: any) {
        throw new Error(`Urbaneez backend error saving asset: ${err.message}`);
      }

      // 2. Second Request: Upload File
      try {
        await axios.put(assetData.links.upload, file);
      } catch (err: any) {
        throw new Error(`S3 storage error uploading file: ${err.message}`);
      }

      // 3. Success
      if (onSuccess) onSuccess(assetData);

    } catch (err: any) {
      // Handle the explicitly prefixed errors
      messageApi.error(err.message);
    } finally {
      setIsLoading(false);
      event.target.value = '';
    }

    // api
    //   .post('/assets', {
    //     name: file.name,
    //     size: file.size,
    //     fileType: file.type,
    //     type: 'file',
    //     blurHash,
    //     meta: {
    //       height: image.height,
    //       width: image.width,
    //     }
    //   })
    //   .then(async ({ data }) => {
    //     await axios.put(data.links.upload, file);
    //     return data;
    //   })
    //   .then(asset => {
    //     if (onSuccess) {
    //       onSuccess(asset);
    //     }
    //   })
    //   .catch(err => {
    //     messageApi.error(err.message)
    //   })
    //   .finally(() => {
    //     setIsLoading(false);
    //   });
  };

  return (
    <Container>
      {contextHolder}

      <Cover src={src}/>
      
      <Button 
        icon={<EditOutlined />} 
        type="default" 
        onClick={openFilePicker} 
        style={{
          position: 'absolute', 
          right: '1.6rem', 
          top: '1.6rem'}}/>

      <FileInput
        ref={inputRef}
        type="file"
        onChange={handleFile}
      />

      {isLoading && (
        <Spin style={{
          position: 'absolute', 
          top: '50%', 
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }}/>
      )}
    </Container>
  );
};

const Container = styled.div`
position: relative;
height: 24rem;
margin-bottom: 2.4rem;
`;

const Cover = styled.img`
height: 100%;
width: 100%;
object-fit: cover;
border-radius: .8rem;
`;

const FileInput = styled.input`
opacity: 0;
height: 0;
width: 0;
`;

const LoadingOverlay = styled.div`
position: absolute;
top: 0;
bottom: 0;
right: 0;
left: 0;
background: rgba(0, 0, 0, .75);
`;
